/**
 * Utility functions for ticker asset type detection and account compatibility
 */

import { isBaseFuturesProduct, isFuturesContract, stripContinuousPrefix } from './futuresContracts';
import { isOptionSymbol } from './optionSymbol';

// Common futures ticker patterns
const FUTURES_PATTERNS = [
  // Micro E-mini futures
  /^MES[A-Z]\d{2}$/, // MESZ24, MESH25, etc.
  /^MNQ[A-Z]\d{2}$/, // MNQZ24, MNQH25, etc.
  /^MYM[A-Z]\d{2}$/, // MYMZ24, MYMH25, etc.
  /^M2K[A-Z]\d{2}$/, // M2KZ24, M2KH25, etc.
  
  // Regular E-mini futures
  /^ES[A-Z]\d{2}$/,  // ESZ24, ESH25, etc.
  /^NQ[A-Z]\d{2}$/,  // NQZ24, NQH25, etc.
  /^YM[A-Z]\d{2}$/,  // YMZ24, YMH25, etc.
  /^RTY[A-Z]\d{2}$/, // RTYZ24, RTYH25, etc.
  
  // Commodities
  /^CL[A-Z]\d{2}$/,  // CLZ24, CLH25, etc.
  /^GC[A-Z]\d{2}$/,  // GCZ24, GCH25, etc.
  /^SI[A-Z]\d{2}$/,  // SIZ24, SIH25, etc.
  
  // Treasury futures
  /^ZB[A-Z]\d{2}$/,  // ZBZ24, ZBH25, etc.
  /^ZN[A-Z]\d{2}$/,  // ZNZ24, ZNH25, etc.
];

// Base futures products (without month/year suffix)
const BASE_FUTURES_PRODUCTS = [
  'MES', 'MNQ', 'MYM', 'M2K', // Micro E-mini
  'ES', 'NQ', 'YM', 'RTY',    // E-mini
  'CL', 'GC', 'SI',           // Commodities
  'ZB', 'ZN'                  // Treasury
];

/**
 * Determine if a ticker is a futures contract
 * @param {string} ticker - The ticker symbol
 * @returns {boolean} - True if futures, false otherwise
 */
export const isFuturesTicker = (ticker) => {
  const upperTicker = stripContinuousPrefix(ticker);
  if (!upperTicker) return false;

  // Check against patterns first
  if (FUTURES_PATTERNS.some(pattern => pattern.test(upperTicker))) {
    return true;
  }

  // Root (`MNQ`, `@MNQ`) or a dated contract whose root we trade
  if (isBaseFuturesProduct(upperTicker)) return true;
  if (isFuturesContract(upperTicker) && isBaseFuturesProduct(upperTicker.slice(0, -3))) {
    return true;
  }

  // Check if it's a base futures product from the local list
  if (BASE_FUTURES_PRODUCTS.includes(upperTicker)) {
    return true;
  }

  return false;
};

/**
 * Determine if a ticker is a stock/ETF
 * @param {string} ticker - The ticker symbol
 * @returns {boolean} - True if stock/ETF, false otherwise
 */
export const isStockTicker = (ticker) => {
  if (!ticker) return false;
  
  // If it's not futures, assume it's stock/ETF
  return !isFuturesTicker(ticker);
};

/**
 * Market-data-only instruments (e.g. TS crypto pairs) can chart but not trade.
 * @param {object|null|undefined} tickerDetails
 */
export const isMarketDataOnlyInstrument = (tickerDetails) => {
  if (!tickerDetails) return false;
  if (tickerDetails.marketDataOnly === true) return true;
  if (tickerDetails.tradeable === false) return true;
  const assetType = String(tickerDetails.AssetType || '').toLowerCase();
  return assetType === 'crypto';
};

/**
 * Get the asset type of a ticker
 * @param {string} ticker - The ticker symbol
 * @param {object} tickerDetails - Optional ticker details object
 * @returns {string} - 'futures', 'stock', or 'crypto'
 */
export const getTickerAssetType = (ticker, tickerDetails = null) => {
  if (isMarketDataOnlyInstrument(tickerDetails)) {
    return 'crypto';
  }
  // Continuous quotes (`@MNQ`) and roots must stay futures even when a symbol
  // lookup returns an equity AssetType. Otherwise the futures account switches away.
  if (isFuturesTicker(ticker)) {
    return 'futures';
  }
  // If we have ticker details, use the AssetType from the details
  if (tickerDetails && tickerDetails.AssetType) {
    const assetType = tickerDetails.AssetType.toLowerCase();
    if (assetType === 'future') {
      return 'futures';
    }
    // Default to stock for other asset types
    return 'stock';
  }
  
  return 'stock';
};

/**
 * Map a ticker to the broker capability flags: equities | options | futures | crypto.
 * Prefers symbol details, then futures ticker patterns. Unknown symbols default to equities
 * so we do not block stock charts while details load.
 */
export const getBrokerAssetClass = (ticker, tickerDetails = null) => {
  if (isMarketDataOnlyInstrument(tickerDetails)) {
    return 'crypto';
  }
  if (isOptionSymbol(ticker)) return 'options';

  const fromDetails = String(
    tickerDetails?.AssetType || tickerDetails?.SecurityType || tickerDetails?.assetClass || ''
  ).toLowerCase();
  if (isFuturesTicker(ticker)) return 'futures';
  if (fromDetails.includes('future')) return 'futures';
  if (fromDetails.includes('option')) return 'options';
  if (fromDetails.includes('crypto')) return 'crypto';
  if (fromDetails) return 'equities';

  return 'equities';
};

/**
 * Check if an account type is compatible with a ticker asset type
 * @param {string} accountType - The account type ('Futures', 'Margin', 'Equity')
 * @param {string} tickerAssetType - The ticker asset type ('futures', 'stock')
 * @returns {boolean} - True if compatible, false otherwise
 */
export const isAccountTickerCompatible = (accountType, tickerAssetType) => {
  if (!accountType || !tickerAssetType) {
    return false;
  }

  // Chart-only symbols are compatible with any account (orders gated separately).
  if (tickerAssetType === 'crypto') {
    return true;
  }

  const normalizedAccountType = accountType.toLowerCase();

  if (tickerAssetType === 'options') {
    return normalizedAccountType !== 'futures';
  }
  
  // Futures accounts can only trade futures
  if (normalizedAccountType === 'futures') {
    return tickerAssetType === 'futures';
  }
  
  // Margin/Equity accounts can trade stocks
  if (normalizedAccountType === 'margin' || normalizedAccountType === 'equity') {
    return tickerAssetType === 'stock';
  }
  
  return false;
};

/**
 * Get a user-friendly message explaining the incompatibility
 * @param {string} accountType - The account type
 * @param {string} tickerAssetType - The ticker asset type
 * @returns {string} - User-friendly message
 */
export const getIncompatibilityMessage = (accountType, tickerAssetType) => {
  if (!accountType || !tickerAssetType) {
    return 'Unable to determine account or ticker type';
  }
  
  if (accountType === 'Futures' && tickerAssetType === 'stock') {
    return 'Futures accounts can only trade futures contracts. Please switch to a margin account to trade stocks.';
  }
  
  if ((accountType === 'Margin' || accountType === 'Equity') && tickerAssetType === 'futures') {
    return 'Margin accounts can only trade stocks and ETFs. Please switch to a futures account to trade futures contracts.';
  }
  
  return 'Account and ticker type are incompatible';
};

/**
 * Get the recommended account type for a ticker
 * @param {string} tickerAssetType - The ticker asset type
 * @returns {string} - Recommended account type
 */
export const getRecommendedAccountType = (tickerAssetType) => {
  if (tickerAssetType === 'futures') {
    return 'Futures';
  }
  // Stocks can be traded with Margin accounts
  return 'Margin';
}; 